import type { Agent, Hook, Validation } from '@lucidtech/las-sdk-browser';
import { useCallback } from 'react';

import { CREATE_VALIDATION_TASK_FUNCTION_ID } from '@/constants';
import {
  toActionId,
  toAgentId,
  toHookId,
  toValidationId,
  useDeleteAction,
  useDeleteHook,
  useDeleteValidation as useDeleteValidationApi,
  useListActions,
  useListHooks,
  useUpdateAgent,
  useUpdateHook,
} from '@/hooks/api';
import { isEmpty } from '@/utils';

export type UseDeleteValidationOnSuccess = (validation: Validation) => void;
export type UseDeleteValidationOnError = ({ error }: { error: Error }) => void;
export type UseDeleteValidationCallbackArgs = {
  agentId: string;
  validationId: string;
  onSuccess?: UseDeleteValidationOnSuccess;
  onError?: UseDeleteValidationOnError;
};
export type UseDeleteValidationCallback = ({
  agentId,
  validationId,
  onSuccess,
  onError,
}: UseDeleteValidationCallbackArgs) => Promise<Validation>;

export type UseDeleteValidationOpts = {
  onSuccess?: UseDeleteValidationOnSuccess;
  onError?: UseDeleteValidationOnError;
};

export const useDeleteValidation = ({
  onSuccess: onSuccessFnBase,
  onError: onErrorFnBase,
}: UseDeleteValidationOpts = {}) => {
  const { data: { data: actions } = {} } = useListActions();
  const { data: { data: hooks } = {} } = useListHooks();

  const {
    mutateAsync: updateAgent,
    isPending: isPendingUpdateAgent,
    isSuccess: isSuccessUpdateAgent,
    error: errorUpdateAgent,
  } = useUpdateAgent();

  const {
    mutateAsync: deleteAction,
    isPending: isPendingDeleteAction,
    isSuccess: isSuccessDeleteAction,
    error: errorDeleteAction,
  } = useDeleteAction();

  const {
    mutateAsync: deleteHook,
    isPending: isPendingDeleteHook,
    isSuccess: isSuccessDeleteHook,
    error: errorDeleteHook,
  } = useDeleteHook();

  const {
    mutateAsync: deleteValidation,
    isPending: isPendingDeleteValidation,
    isSuccess: isSuccessDeleteValidation,
    error: errorDeleteValidation,
  } = useDeleteValidationApi();

  const {
    mutateAsync: updateHook,
    isPending: isPendingUpdateHook,
    isSuccess: isSuccessUpdateHook,
    error: errorUpdateHook,
  } = useUpdateHook();

  const deleteValidationFn: UseDeleteValidationCallback = useCallback(
    async ({ agentId, validationId, onSuccess: onSuccessFn, onError: onErrorFn }: UseDeleteValidationCallbackArgs) => {
      const onSuccess: UseDeleteValidationOnSuccess = (...args) => {
        onSuccessFnBase?.(...args);
        onSuccessFn?.(...args);
      };
      const onError: UseDeleteValidationOnError = (...args) => {
        onErrorFnBase?.(...args);
        onErrorFn?.(...args);
      };

      try {
        const relatedActionIds =
          actions
            ?.filter((action) => {
              return (
                action.functionId === CREATE_VALIDATION_TASK_FUNCTION_ID && action.config.validationId === validationId
              );
            })
            .map((action) => action.actionId) ?? [];

        const deletedHookIds: string[] = [];

        for (const hook of hooks ?? []) {
          const updates: Partial<Hook> = {};

          if (hook.trueActionId && relatedActionIds.includes(hook.trueActionId)) {
            updates.trueActionId = null;
          }

          if (hook.falseActionId && relatedActionIds.includes(hook.falseActionId)) {
            updates.falseActionId = null;
          }

          if (!isEmpty(updates)) {
            const afterUpdate = { ...hook, ...updates } as Hook;
            if ([afterUpdate.trueActionId, afterUpdate.falseActionId].every((id) => id == null)) {
              await deleteHook(toHookId(hook.hookId));
              deletedHookIds.push(hook.hookId);
            } else {
              await updateHook({ id: toHookId(hook.hookId), updates });
            }
          }
        }

        for (const actionId of relatedActionIds) {
          await deleteAction(toActionId(actionId));
        }

        const validation = await deleteValidation(toValidationId(validationId));

        await updateAgent({
          id: toAgentId(agentId),
          updates: (agent: Agent) => ({
            resourceIds: [
              ...agent.resourceIds.filter((id) => !relatedActionIds.includes(id) && !deletedHookIds.includes(id)),
            ],
          }),
        });

        onSuccess?.(validation);

        return validation;
      } catch (error) {
        if (error instanceof Error) {
          onError?.({ error });
        } else {
          onError?.({ error: new Error('An unknown error occurred') });
        }
        throw error;
      }
    },
    [
      onSuccessFnBase,
      onErrorFnBase,
      actions,
      deleteValidation,
      updateAgent,
      hooks,
      deleteHook,
      updateHook,
      deleteAction,
    ]
  );

  return {
    deleteValidation: deleteValidationFn,
    isPending:
      isPendingDeleteHook ||
      isPendingUpdateHook ||
      isPendingDeleteAction ||
      isPendingDeleteValidation ||
      isPendingUpdateAgent,
    isSuccess:
      isSuccessDeleteHook ||
      isSuccessUpdateHook ||
      isSuccessDeleteAction ||
      isSuccessDeleteValidation ||
      isSuccessUpdateAgent,
    errors: [errorDeleteHook, errorUpdateHook, errorDeleteAction, errorDeleteValidation, errorUpdateAgent].filter(
      (e) => !!e
    ),
  };
};
